import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Trophy, PartyPopper, Recycle, AlertTriangle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GoodJobDialog — iter-78 pt 12.
 *
 * Dual-purpose modal:
 *   • Rewards mode (endShift=false) — celebratory "Good job!" popup shown
 *     after a Site Agent finishes a job or clocks out mid-shift.
 *   • End-Shift mode (endShift=true) — same summary card, plus a
 *     "Confirm & Save · End Shift" button that closes the shift via
 *     POST /api/ops/end-shift so the counters are officially snapshotted.
 */
export function GoodJobDialog({ open, onClose, userName, endShift = false, onConfirmed }) {
  const [counters, setCounters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openedAt] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/rewards/daily/me`, { credentials: 'include' });
        if (!cancelled && r.ok) setCounters(await r.json());
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open]);

  const total = counters
    ? (counters.pickups || 0) + (counters.recycle || 0) + (counters.no_trashes || 0)
    : 0;
  const hours = Math.max(0, Math.round(((Date.now() - openedAt) / 3.6e6) * 10) / 10);

  const confirmEndShift = async () => {
    if (!counters) return;
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ops/end-shift`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trash: counters.pickups || 0,
          recycle: counters.recycle || 0,
          violations: counters.violations || 0,
          no_trashes: counters.no_trashes || 0,
          hours,
        }),
      });
      if (r.ok) {
        toast.success('Shift closed. Great work!');
        onConfirmed?.();
        onClose?.();
      } else {
        toast.error('Could not close the shift — try again.');
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-md bg-zinc-950 border-2 border-[#C9A962] text-white"
        data-testid={endShift ? 'end-shift-dialog' : 'good-job-dialog'}
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2 text-2xl"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#FFD60A' }}
            data-testid="good-job-title"
          >
            <PartyPopper className="h-6 w-6 text-[#FFD60A]" />
            {endShift
              ? `End Shift · ${userName?.split(' ')[0] || 'Champion'}`
              : `Good job, ${userName?.split(' ')[0] || 'Champion'}!`}
          </DialogTitle>
        </DialogHeader>
        {loading || !counters ? (
          <p className="text-sm text-zinc-400 py-6 text-center">Tallying today&apos;s work…</p>
        ) : (
          <div className="space-y-4 py-2" data-testid="good-job-body">
            <p className="text-sm text-zinc-300">
              You completed{' '}
              <strong className="text-emerald-400" data-testid="good-job-pickups">{counters.pickups || 0}</strong> pickups,{' '}
              <strong className="text-blue-300" data-testid="good-job-recycle">{counters.recycle || 0}</strong> recycle,{' '}
              <strong className="text-red-400" data-testid="good-job-violations">{counters.violations || 0}</strong> violations, and{' '}
              <strong className="text-orange-300" data-testid="good-job-no-trashes">{counters.no_trashes || 0}</strong> no-trashes today.
            </p>

            <div className="grid grid-cols-2 gap-2">
              <Tile tone="emerald" icon={Trophy} value={counters.pickups || 0} label="Trash" />
              <Tile tone="blue"    icon={Recycle} value={counters.recycle || 0} label="Recycle" />
              <Tile tone="red"     icon={AlertTriangle} value={counters.violations || 0} label="Violations" />
              <Tile tone="orange"  icon={XCircle} value={counters.no_trashes || 0} label="No-trash" />
              {endShift && (
                <div className="rounded-md border border-[#C9A962]/40 bg-[#C9A962]/10 p-3 text-center col-span-2">
                  <Clock className="h-4 w-4 mx-auto mb-1 text-[#C9A962]" />
                  <p className="text-3xl font-black text-[#C9A962]" data-testid="end-shift-hours">{hours.toFixed(1)} h</p>
                  <p className="text-[10px] uppercase tracking-widest text-[#C9A962] mt-1">Time worked</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-zinc-400 pt-1">
              <span>Total serviced today</span>
              <Badge className="bg-[#C9A962] text-black font-bold">{total}</Badge>
            </div>

            {endShift ? (
              <Button
                onClick={confirmEndShift}
                disabled={saving}
                className="w-full bg-[#FFD60A] hover:bg-[#EAB308] text-black font-black py-3 text-base"
                data-testid="confirm-end-shift-btn"
              >
                {saving ? 'Saving…' : 'Confirm & Save · End Shift'}
              </Button>
            ) : (
              <p className="text-[10px] text-zinc-500 leading-snug text-center pt-2">
                Weekly, monthly &amp; yearly rollups feed your bonus calc automatically. Keep it up!
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TONE = {
  emerald: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  blue:    'border-blue-400/40 bg-blue-400/10 text-blue-200',
  red:     'border-red-500/40 bg-red-500/10 text-red-300',
  orange:  'border-orange-500/40 bg-orange-500/10 text-orange-300',
};
function Tile({ tone, icon: Icon, value, label }) {
  return (
    <div className={`rounded-md border p-3 text-center ${TONE[tone]}`}>
      <Icon className="h-4 w-4 mx-auto mb-1 opacity-80" />
      <p className="text-3xl font-black">{value}</p>
      <p className="text-[10px] uppercase tracking-widest mt-1">{label}</p>
    </div>
  );
}

export default GoodJobDialog;
